import { useClickOutside, useHotkeys } from "@mantine/hooks";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { spacing } from "#app/theme/v2/spacing";
import { IconButton } from "#components/atoms/IconButton/IconButton";
import { SearchBar } from "#components/atoms/SearchBar/SearchBar";
import { Tooltip } from "#components/atoms/Tooltip/Tooltip";
import { Icon } from "#shared/icons/Icon";
import { Flex } from "#shared/view/components/Flex";

import classes from "./QuoteRequestLinesSearchField.module.css";

type Props = {
  value: string;
  onChange: (value: string) => void;
};

export const QuoteRequestLinesSearchField = ({ value, onChange }: Props) => {
  const { t } = useTranslation();
  const [isManuallyOpened, setIsManuallyOpened] = useState(false);
  const isOpen = isManuallyOpened || value.length > 0;

  const close = () => {
    setIsManuallyOpened(false);
    onChange("");
  };

  const containerRef = useClickOutside(() => {
    if (!value) {
      setIsManuallyOpened(false);
    }
  });

  useHotkeys([["mod+F", () => setIsManuallyOpened(true)]], []);

  if (!isOpen) {
    return (
      <Tooltip label={t("quoteRequestV2.header.search")} position="bottom">
        <IconButton
          variant="secondary"
          size="sm"
          aria-label={t("quoteRequestV2.header.search")}
          onClick={() => setIsManuallyOpened(true)}
        >
          <Icon name="search" size={16} />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Flex
      ref={containerRef}
      gap={spacing[2]}
      align="center"
      className={classes.container}
      onKeyDown={(e) => e.key === "Escape" && close()}
    >
      <SearchBar
        value={value}
        onChange={onChange}
        onClear={() => onChange("")}
        placeholder={t("quoteRequestV2.header.searchPlaceholder")}
        autoFocus
      />
      <IconButton
        variant="secondary"
        size="sm"
        aria-label={t("quoteRequestV2.header.searchClose")}
        onClick={close}
      >
        <Icon name="x" size={16} />
      </IconButton>
    </Flex>
  );
};
